import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Typography,
    Switch,
    FormControlLabel,
    FormGroup,
    Divider,
    Alert
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

interface Module {
    id: string;
    name: string;
    description: string;
    permissions: string[];
}

interface Scope {
    id: string;
    name: string;
    description: string;
}

interface User {
    _id: string;
    name: string;
    email: string;
}

interface ModuleAccessDialogProps {
    open: boolean;
    onClose: () => void;
    selectedUser: User | null;
    onSave: (data: any) => Promise<void>;
    availableModules: Module[];
    availableScopes: Scope[];
}

interface ModulePermission {
    module: string;
    permissions: string[];
    scope: string;
}

interface ContextPermissions {
    isManager: boolean;
    canApproveLeaves: boolean;
    canApproveAttendance: boolean;
    canApproveTimesheets: boolean;
    canApproveExpenses: boolean;
    approvalScope: string;
    canAssignRoles: boolean;
    maxRoleLevel: number;
}

const ModuleAccessDialog: React.FC<ModuleAccessDialogProps> = ({
    open,
    onClose,
    selectedUser,
    onSave,
    availableModules,
    availableScopes
}) => {
    const [selectedModule, setSelectedModule] = useState<string>('');
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [selectedScope, setSelectedScope] = useState<string>('self');
    const [currentModules, setCurrentModules] = useState<ModulePermission[]>([]);
    const [contextPermissions, setContextPermissions] = useState<ContextPermissions>({
        isManager: false,
        canApproveLeaves: false,
        canApproveAttendance: false,
        canApproveTimesheets: false,
        canApproveExpenses: false,
        approvalScope: 'subordinates',
        canAssignRoles: false,
        maxRoleLevel: 0
    });
    const [reason, setReason] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const handleModuleChange = (event: SelectChangeEvent<string>) => {
        setSelectedModule(event.target.value);
        setSelectedPermissions([]);
    };

    const handlePermissionChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedPermissions(typeof value === 'string' ? value.split(',') : value);
    };

    const handleScopeChange = (event: SelectChangeEvent<string>) => {
        setSelectedScope(event.target.value);
    };

    const handleAddModule = () => {
        if (!selectedModule || selectedPermissions.length === 0) {
            setError('Please select a module and at least one permission');
            return;
        }

        const existingIndex = currentModules.findIndex(m => m.module === selectedModule);
        const newModule: ModulePermission = {
            module: selectedModule,
            permissions: selectedPermissions,
            scope: selectedScope
        };

        if (existingIndex >= 0) {
            const updated = [...currentModules];
            updated[existingIndex] = newModule;
            setCurrentModules(updated);
        } else {
            setCurrentModules([...currentModules, newModule]);
        }

        // Reset form
        setSelectedModule('');
        setSelectedPermissions([]);
        setSelectedScope('self');
        setError('');
    };

    const handleRemoveModule = (module: string) => {
        setCurrentModules(currentModules.filter(m => m.module !== module));
    };

    const handleContextPermissionChange = (field: keyof ContextPermissions, value: boolean | string | number) => {
        setContextPermissions(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');

        try {
            // Save module permissions
            for (const moduleData of currentModules) {
                await onSave({
                    type: 'module',
                    userId: selectedUser?._id,
                    data: moduleData,
                    reason
                });
            }

            // Save context permissions
            if (Object.values(contextPermissions).some(val => val)) {
                await onSave({
                    type: 'context',
                    userId: selectedUser?._id,
                    data: contextPermissions,
                    reason
                });
            }

            onClose();
            resetForm();
        } catch (err: any) {
            setError(err.message || 'Failed to save permissions');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedModule('');
        setSelectedPermissions([]);
        setSelectedScope('self');
        setCurrentModules([]);
        setContextPermissions({
            isManager: false,
            canApproveLeaves: false,
            canApproveAttendance: false,
            canApproveTimesheets: false,
            canApproveExpenses: false,
            approvalScope: 'subordinates',
            canAssignRoles: false,
            maxRoleLevel: 0
        });
        setReason('');
        setError('');
    };

    const getModuleName = (moduleId: string) => {
        const module = availableModules.find(m => m.id === moduleId);
        return module ? module.name : moduleId;
    };

    const getScopeName = (scopeId: string) => {
        const scope = availableScopes.find(s => s.id === scopeId);
        return scope ? scope.name : scopeId;
    };

    const selectedModuleData = availableModules.find(m => m.id === selectedModule);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { minHeight: '600px' }
            }}
        >
            <DialogTitle>
                Manage Module Access - {selectedUser?.name}
                <Typography variant="body2" color="textSecondary">
                    {selectedUser?.email}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                        Module Permissions
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Module</InputLabel>
                                <Select
                                    value={selectedModule}
                                    onChange={handleModuleChange}
                                    label="Module"
                                >
                                    {availableModules.map((module) => (
                                        <MenuItem key={module.id} value={module.id}>
                                            {module.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={!selectedModule}>
                                <InputLabel>Permissions</InputLabel>
                                <Select
                                    multiple
                                    value={selectedPermissions}
                                    onChange={handlePermissionChange}
                                    label="Permissions"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {selectedModuleData?.permissions.map((permission) => (
                                        <MenuItem key={permission} value={permission}>
                                            {permission}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Scope</InputLabel>
                                <Select
                                    value={selectedScope}
                                    onChange={handleScopeChange}
                                    label="Scope"
                                >
                                    {availableScopes.map((scope) => (
                                        <MenuItem key={scope.id} value={scope.id}>
                                            {scope.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                onClick={handleAddModule}
                                disabled={!selectedModule || selectedPermissions.length === 0}
                                fullWidth
                                size="small"
                            >
                                Add
                            </Button>
                        </Grid>
                    </Grid>

                    {selectedModuleData && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {selectedModuleData.description}
                        </Typography>
                    )}
                </Box>

                {currentModules.length > 0 && (
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            Current Module Access:
                        </Typography>
                        {currentModules.map((module, index) => (
                            <Box key={index} sx={{ mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {getModuleName(module.module)} ({getScopeName(module.scope)})
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            {module.permissions.map((permission) => (
                                                <Chip
                                                    key={permission}
                                                    label={permission}
                                                    size="small"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveModule(module.module)}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                        Manager Permissions
                    </Typography>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={contextPermissions.isManager}
                                    onChange={(e) => handleContextPermissionChange('isManager', e.target.checked)}
                                />
                            }
                            label="Is Manager"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={contextPermissions.canApproveLeaves}
                                    onChange={(e) => handleContextPermissionChange('canApproveLeaves', e.target.checked)}
                                />
                            }
                            label="Can Approve Leaves"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={contextPermissions.canApproveAttendance}
                                    onChange={(e) => handleContextPermissionChange('canApproveAttendance', e.target.checked)}
                                />
                            }
                            label="Can Approve Attendance"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={contextPermissions.canApproveTimesheets}
                                    onChange={(e) => handleContextPermissionChange('canApproveTimesheets', e.target.checked)}
                                />
                            }
                            label="Can Approve Timesheets"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={contextPermissions.canApproveExpenses}
                                    onChange={(e) => handleContextPermissionChange('canApproveExpenses', e.target.checked)}
                                />
                            }
                            label="Can Approve Expenses"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={contextPermissions.canAssignRoles}
                                    onChange={(e) => handleContextPermissionChange('canAssignRoles', e.target.checked)}
                                />
                            }
                            label="Can Assign Roles to Others"
                        />
                    </FormGroup>

                    {(contextPermissions.canApproveLeaves || 
                      contextPermissions.canApproveAttendance || 
                      contextPermissions.canApproveTimesheets ||
                      contextPermissions.canApproveExpenses) && (
                        <Box sx={{ mt: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Approval Scope</InputLabel>
                                <Select
                                    value={contextPermissions.approvalScope}
                                    onChange={(e) => handleContextPermissionChange('approvalScope', e.target.value)}
                                    label="Approval Scope"
                                >
                                    <MenuItem value="subordinates">Direct Subordinates</MenuItem>
                                    <MenuItem value="department">Department</MenuItem>
                                    <MenuItem value="all">All Users</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </Box>

                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        Reason for Changes (Optional)
                    </Typography>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for permission changes..."
                        style={{
                            width: '100%',
                            minHeight: '60px',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontFamily: 'inherit'
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || (currentModules.length === 0 && !Object.values(contextPermissions).some(val => val))}
                >
                    {loading ? 'Saving...' : 'Save Permissions'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModuleAccessDialog;